/*
 * RequireAppInstalled — small route guard.
 *
 * Wraps a page that depends on a specific app being installed. If the
 * app is not in the user's installed list, redirects to the App Store
 * (with a one-shot toast explaining why) — so a stale URL or browser
 * back-button can't bypass the uninstall.
 *
 * Loads `/api/apps` once on mount (cheap — same endpoint the sidebar
 * already calls). Shows a tiny loading shell during the fetch so we
 * never render the protected page even for a frame.
 */
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function RequireAppInstalled({ appId, children }) {
  const { user } = useAuth();
  const [state, setState] = useState({ loading: true, installed: false });

  useEffect(() => {
    let alive = true;
    api.get("/apps").then(r => {
      if (!alive) return;
      const app = (r.data || []).find(a => a.id === appId);
      const installed = !!(app && app.installed);
      setState({ loading: false, installed });
      if (!installed) {
        toast.error(`The ${app?.name || appId} app isn't installed — install it first to access this page.`);
      }
    }).catch(() => {
      if (!alive) return;
      // On API failure, fail-open so the page still loads (prefer UX over false-negatives).
      setState({ loading: false, installed: true });
    });
    return () => { alive = false; };
  }, [appId]);

  if (state.loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-foreground/40">
        <Loader2 className="w-5 h-5 animate-spin"/>
      </div>
    );
  }
  if (!state.installed) {
    const target = user?.role === "seller" ? "/seller/apps" : "/buyer/apps";
    return <Navigate to={target} replace/>;
  }
  return children;
}
